import { Avatar, Badge, Card, Group, Text } from "@mantine/core";
import { useIsMutating, useIsFetching } from "@tanstack/react-query";
import React, { KeyboardEvent } from "react";
import { Draggable } from "react-beautiful-dnd";

import {
  BugIcon,
  HighPriorityIcon,
  LowPriorityIcon,
  MediumPriorityIcon,
  StoryIcon,
  TaskIcon,
} from "assets/icons";
import { User } from "types";
import { capitalizeFirstLetter } from "utils/capitalizeFirstLetter";
import { getInitials } from "utils/getInitials";

import styles from "./IssueCard.module.css";
import { Calendar } from "tabler-icons-react";

type IssueCardProps = {
  id: string;
  index: number;
  title: string;
  type: "task" | "story" | "bug";
  priority: "low" | "medium" | "high";
  assigneeId: string | undefined;
  reporterId: string | undefined;
  dueDate: string | undefined;
  members: User<string>[];
  onClick?: () => void;
};

const typeIcons: Record<IssueCardProps["type"], React.ReactNode> = {
  task: <TaskIcon />,
  story: <StoryIcon />,
  bug: <BugIcon />,
};

const priorityIcons: Record<IssueCardProps["priority"], React.ReactNode> = {
  low: <LowPriorityIcon />,
  medium: <MediumPriorityIcon />,
  high: <HighPriorityIcon />,
};

function IssueCard({
  id,
  index,
  title,
  type,
  priority,
  assigneeId,
  reporterId,
  members,
  dueDate,
  onClick,
}: IssueCardProps) {
  const isMutating = useIsMutating();
  const isFetchingProject = useIsFetching({ queryKey: ["projects"] });
  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.defaultPrevented) {
      return;
    }

    if (e.key === "Enter" && onClick) {
      onClick();
    }
  };

  return (
    <Draggable
      draggableId={id}
      index={index}
      isDragDisabled={isMutating > 0 || isFetchingProject > 0}
    >
      {(provided) => (
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions
        <div
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          ref={provided.innerRef}
          className={styles.draggable}
          onClick={onClick}
          onKeyDown={handleKeyDown}
        >
          <Card withBorder radius="md" p="md">
            <Group position="apart" mb="xs">
              <Group spacing={4}>
                {typeIcons[type]}
                <Badge variant="light">{capitalizeFirstLetter(type)}</Badge>
              </Group>
              <Group spacing={4}>
                {priorityIcons[priority]}
                <Badge color="red" variant="light">
                  {capitalizeFirstLetter(priority)}
                </Badge>
              </Group>
            </Group>
            <Text weight={500}>{title}</Text>
            <Group spacing="xs" mb="sm">
              <Group spacing={4}>
                <Calendar size={16} />
                <Text size="xs">
                  {new Date(dueDate ?? Date.now()).toLocaleDateString("en-US", {
                    month: "long",
                    day: "numeric",
                  })}
                </Text>
              </Group>
            </Group>
            <Group position="apart">
              <Group spacing="xs">
                {members
                  .filter((item) => [assigneeId, reporterId].includes(item._id))
                  .map((user) => (
                    <Avatar radius="xl" size="sm" key={user._id}>
                      {getInitials(user.firstName, user.lastName)}
                    </Avatar>
                  ))}
              </Group>
            </Group>
          </Card>
        </div>
      )}
    </Draggable>
  );
}

export default IssueCard;
